import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { isVehiclePhoto, resolveDocumentUrl } from "../database/documentUrl";
import { search as searchVehicules } from "../database/vehiculeService";
import { deleteVhuLot, loadVhuLots, saveVhuLot } from "./vhuLotService";

const STATUS_OPTIONS = [
  { value: "BROUILLON", label: "Brouillon" },
  { value: "OUVERT", label: "Ouvert" },
  { value: "CLOTURE", label: "Cloture" },
  { value: "ANNULE", label: "Annule" },
];

const statusColor = {
  BROUILLON: "secondary",
  OUVERT: "success",
  CLOTURE: "dark",
  ANNULE: "danger",
};

const statusLabel = (status) => STATUS_OPTIONS.find((item) => item.value === status)?.label || status || "-";

const toDatetimeLocal = (date) => {
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
};

const createEmptyForm = () => {
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);

  return {
    reference: "",
    titre: "",
    description: "",
    status: "OUVERT",
    dateDebut: toDatetimeLocal(now),
    dateFin: toDatetimeLocal(tomorrow),
    prixDepart: "",
    pasMinimal: "100",
    estimationDepart: "",
    lieuStockage: "",
    vehiculeIds: [],
    isActif: true,
  };
};

const asNumberOrNull = (value) => (value === "" || value === null || value === undefined ? null : Number(value));
const vehicleLabel = (vehicle) => [vehicle.marque, vehicle.modele, vehicle.immatriculation || vehicle.numChassis].filter(Boolean).join(" - ") || `Vehicule #${vehicle.id}`;
const vehiclePhotos = (vehicle) => ((vehicle?.photos || vehicle?.documents || []).filter(isVehiclePhoto));
const lotPhotos = (lot) => (lot.vehicules || []).flatMap(vehiclePhotos);
const moneyLabel = (value) => `${Number(value || 0).toLocaleString("fr-FR")} MAD`;
const dateLabel = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const hasTime = String(value).includes("T");
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    ...(hasTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  }).format(date);
};
const isMobileVisible = (lot) => {
  const now = new Date();
  const start = lot.dateDebut ? new Date(lot.dateDebut) : null;
  const end = lot.dateFin ? new Date(lot.dateFin) : null;
  return lot.isActif !== false && lot.status === "OUVERT" && (!start || start <= now) && (!end || end > now);
};

function VhuLotsAdminPage() {
  const [lots, setLots] = useState([]);
  const [vehicules, setVehicules] = useState([]);
  const [form, setForm] = useState(createEmptyForm);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [vehicleQuery, setVehicleQuery] = useState("");
  const [photoViewer, setPhotoViewer] = useState({ open: false, photos: [], index: 0, title: "" });

  useEffect(() => {
    loadPageData();
  }, []);

  const loadPageData = async () => {
    setLoading(true);
    try {
      const [lotResponse, vehiculeResponse] = await Promise.all([
        loadVhuLots(),
        searchVehicules({ criteria: {}, page: 0, size: 300 }),
      ]);
      setLots(lotResponse?.data?.body || []);
      setVehicules(vehiculeResponse?.data?.body?.content || []);
    } finally {
      setLoading(false);
    }
  };

  const selectedVehicles = useMemo(() => {
    const selectedIds = new Set(form.vehiculeIds || []);
    return vehicules.filter((vehicle) => selectedIds.has(vehicle.id));
  }, [form.vehiculeIds, vehicules]);

  const filteredVehicules = useMemo(() => {
    const query = vehicleQuery.trim().toLowerCase();
    if (!query) return vehicules;
    return vehicules.filter((vehicle) => vehicleLabel(vehicle).toLowerCase().includes(query));
  }, [vehicules, vehicleQuery]);

  const openNewLotForm = () => {
    setForm(createEmptyForm());
    setVehicleQuery("");
    setFormModalOpen(true);
  };

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleVehicle = (id) => {
    setForm((prev) => {
      const current = prev.vehiculeIds || [];
      return {
        ...prev,
        vehiculeIds: current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
      };
    });
  };

  const openPhotoViewer = (photos, title, index = 0) => {
    if (!photos.length) return;
    setPhotoViewer({ open: true, title, photos, index });
  };

  const editLot = (lot) => {
    setForm({
      ...createEmptyForm(),
      ...lot,
      dateDebut: lot.dateDebut || "",
      dateFin: lot.dateFin || "",
      prixDepart: lot.prixDepart || "",
      pasMinimal: lot.pasMinimal || "100",
      estimationDepart: lot.estimationDepart || "",
      vehiculeIds: lot.vehiculeIds || [],
    });
    setVehicleQuery("");
    setFormModalOpen(true);
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await saveVhuLot({
        ...form,
        prixDepart: asNumberOrNull(form.prixDepart),
        pasMinimal: asNumberOrNull(form.pasMinimal),
        estimationDepart: asNumberOrNull(form.estimationDepart),
      });
      setForm(createEmptyForm());
      setFormModalOpen(false);
      await loadPageData();
    } finally {
      setSaving(false);
    }
  };

  const removeLot = async (lot) => {
    if (!window.confirm(`Supprimer le lot ${lot.reference || lot.titre || lot.id} ?`)) return;
    await deleteVhuLot(lot.id);
    await loadPageData();
  };

  const renderVehicleThumb = (vehicle, size = 56) => {
    const photos = vehiclePhotos(vehicle);
    const photo = photos[0];
    if (!photo) {
      return (
        <span className="vhu-thumb vhu-thumb-empty" style={{ width: size, height: size }}>
          <i className="ri-car-line fs-4 text-muted" />
        </span>
      );
    }
    return (
      <button
        type="button"
        className="vhu-thumb-button"
        onClick={(event) => {
          event.stopPropagation();
          openPhotoViewer(photos, vehicleLabel(vehicle));
        }}
      >
        <img src={resolveDocumentUrl(photo)} alt={photo?.filenameUser || photo?.filename || vehicleLabel(vehicle)} className="vhu-thumb" style={{ width: size, height: size }} />
        {photos.length > 1 && (
          <span className="vhu-photo-count">{photos.length}</span>
        )}
      </button>
    );
  };

  return (
    <div className="page-content auction-page vhu-lots-page">
      <Container fluid>
        <Breadcrumbs
          title="Lots VHU"
          breadcrumbItems={[{ title: "Modules", link: "#" }, { title: "Lots VHU", link: "#" }]}
          actions={(
            <Button color="primary" onClick={openNewLotForm}>
              <i className="ri-add-line me-1" />
              Nouveau lot
            </Button>
          )}
        />

        <Row className="g-4">
          <Col xl={12}>
            {loading ? (
              <div className="text-center py-5"><Spinner color="primary" /></div>
            ) : !lots.length ? (
              <div className="vhu-empty-state">
                <i className="ri-inbox-line" />
                <strong>Aucun lot a afficher</strong>
                <span>Creez un nouveau lot VHU pour commencer.</span>
              </div>
            ) : (
              <Row className="g-3">
                {lots.map((lot) => {
                  const photos = lotPhotos(lot);
                  return (
                    <Col md={6} key={lot.id}>
                      <Card className="h-100 auction-card vhu-lot-card">
                        <CardBody>
                          <div className="vhu-card-photo-row">
                            {(lot.vehicules || []).slice(0, 5).map((vehicle) => <span key={vehicle.id}>{renderVehicleThumb(vehicle, 58)}</span>)}
                            {!photos.length && <span className="vhu-empty-photo"><i className="ri-image-line" /> Sans photo</span>}
                          </div>
                          <div className="d-flex justify-content-between gap-3 mb-3">
                            <div>
                              <h5 className="mb-1">{lot.titre}</h5>
                              <div className="text-muted small">{lot.reference || `Lot #${lot.id}`} - {lot.lieuStockage || "Lieu non renseigne"}</div>
                            </div>
                            <div className="d-grid gap-1 justify-items-end">
                              <Badge color={statusColor[lot.status] || "secondary"}>{statusLabel(lot.status)}</Badge>
                              {isMobileVisible(lot) && <Badge color="success" className="vhu-mobile-badge">Mobile</Badge>}
                            </div>
                          </div>
                          <Row className="g-2 mb-3 vhu-card-metrics">
                            <Col xs={6}><span className="text-muted small d-block">Vehicules</span><strong>{lot.vehicules?.length || 0}</strong></Col>
                            <Col xs={6}><span className="text-muted small d-block">Meilleure offre</span><strong>{moneyLabel(lot.currentBestBid)}</strong></Col>
                            <Col xs={6}><span className="text-muted small d-block">Fin</span><strong>{dateLabel(lot.dateFin)}</strong></Col>
                            <Col xs={6}><span className="text-muted small d-block">Offres</span><strong>{lot.bidsCount || 0}</strong></Col>
                          </Row>
                          <div className="small text-muted mb-3">
                            {lot.bestBidderNom ? `Meilleur acheteur: ${lot.bestBidderNom}` : "Aucune offre recue pour le moment"}
                          </div>
                          <div className="d-flex flex-wrap gap-2">
                            <Button size="sm" color="light" onClick={() => editLot(lot)}>Modifier</Button>
                            <Button size="sm" color="light" disabled={!photos.length} onClick={() => openPhotoViewer(photos, lot.titre || "Photos du lot")}>Voir photos</Button>
                            <Button size="sm" color="light" className="text-danger" onClick={() => removeLot(lot)}>Supprimer</Button>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </Col>
        </Row>
      </Container>

      <Modal isOpen={formModalOpen} toggle={() => setFormModalOpen(false)} size="xl" backdrop="static">
        <form onSubmit={submit}>
          <ModalHeader toggle={() => setFormModalOpen(false)}>
            {form.id ? "Modifier le lot VHU" : "Nouveau lot VHU"}
          </ModalHeader>
          <ModalBody>
            <Row className="g-3">
              <Col xs={12}><div className="vhu-section-label">Informations</div></Col>
              <Col md={5}>
                <Label>Reference</Label>
                <Input value={form.reference || ""} onChange={(event) => setField("reference", event.target.value)} placeholder="VHU-2026-001" />
              </Col>
              <Col md={7}>
                <Label>Titre</Label>
                <Input value={form.titre || ""} onChange={(event) => setField("titre", event.target.value)} placeholder="Lot utilitaires sinistres" required />
              </Col>
              <Col md={3}>
                <Label>Status</Label>
                <Input type="select" value={form.status} onChange={(event) => setField("status", event.target.value)}>
                  {STATUS_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </Input>
              </Col>
              <Col md={3}>
                <Label>Lieu stockage</Label>
                <Input value={form.lieuStockage || ""} onChange={(event) => setField("lieuStockage", event.target.value)} />
              </Col>
              <Col md={3}>
                <Label>Date debut</Label>
                <Input type="datetime-local" value={form.dateDebut || ""} onChange={(event) => setField("dateDebut", event.target.value)} />
              </Col>
              <Col md={3}>
                <Label>Date fin</Label>
                <Input type="datetime-local" value={form.dateFin || ""} onChange={(event) => setField("dateFin", event.target.value)} />
              </Col>
              <Col md={4}>
                <Label>Prix depart</Label>
                <Input type="number" min="0" value={form.prixDepart} onChange={(event) => setField("prixDepart", event.target.value)} />
              </Col>
              <Col md={4}>
                <Label>Pas minimal</Label>
                <Input type="number" min="0" value={form.pasMinimal} onChange={(event) => setField("pasMinimal", event.target.value)} />
              </Col>
              <Col md={4}>
                <Label>Estimation</Label>
                <Input type="number" min="0" value={form.estimationDepart} onChange={(event) => setField("estimationDepart", event.target.value)} />
              </Col>
              <Col xs={12}>
                <Label>Description</Label>
                <Input type="textarea" rows="3" value={form.description || ""} onChange={(event) => setField("description", event.target.value)} />
              </Col>

              <Col lg={7}>
                <div className="vhu-section-label">Vehicules du lot</div>
                <div className="vhu-search-control">
                  <i className="ri-search-line" />
                  <Input value={vehicleQuery} onChange={(event) => setVehicleQuery(event.target.value)} placeholder="Rechercher marque, modele, immatriculation..." />
                </div>
                <div className="vhu-vehicle-list">
                  {filteredVehicules.map((vehicle) => {
                    const selected = form.vehiculeIds.includes(vehicle.id);
                    const photos = vehiclePhotos(vehicle);
                    return (
                      <button type="button" key={vehicle.id} className={`vhu-vehicle-option ${selected ? "active" : ""}`} onClick={() => toggleVehicle(vehicle.id)}>
                        <div className="d-flex gap-2 align-items-center">
                          {renderVehicleThumb(vehicle)}
                          <div className="flex-grow-1">
                            <div className="fw-semibold">{vehicleLabel(vehicle)}</div>
                            <div className="small text-muted">{photos.length} photo(s) - {vehicle.carburant || "Carburant non renseigne"}</div>
                          </div>
                          {selected && <i className="ri-check-line fs-4 text-primary" />}
                        </div>
                      </button>
                    );
                  })}
                  {!filteredVehicules.length && (
                    <div className="vhu-empty-note">Aucun vehicule ne correspond a cette recherche.</div>
                  )}
                </div>
              </Col>
              <Col lg={5}>
                <div className="vhu-section-label">Selection</div>
                <div className="vhu-selected-strip h-100">
                  <div className="fw-semibold mb-2">{selectedVehicles.length} vehicule(s) selectionne(s)</div>
                  <div className="d-flex flex-wrap gap-2">
                    {selectedVehicles.slice(0, 12).map((vehicle) => <span key={vehicle.id}>{renderVehicleThumb(vehicle, 52)}</span>)}
                    {selectedVehicles.length > 12 && <span className="small text-muted align-self-center">+{selectedVehicles.length - 12}</span>}
                  </div>
                </div>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="light" type="button" onClick={() => setFormModalOpen(false)}>Annuler</Button>
            <Button color="primary" type="submit" disabled={saving}>
              {saving && <Spinner size="sm" className="me-2" />}
              {form.id ? "Mettre a jour" : "Creer le lot"}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {photoViewer.open && photoViewer.photos.length > 0 && (
        <Lightbox
          mainSrc={resolveDocumentUrl(photoViewer.photos[photoViewer.index])}
          nextSrc={resolveDocumentUrl(photoViewer.photos[(photoViewer.index + 1) % photoViewer.photos.length])}
          prevSrc={resolveDocumentUrl(photoViewer.photos[(photoViewer.index + photoViewer.photos.length - 1) % photoViewer.photos.length])}
          imageTitle={photoViewer.title}
          imageCaption={photoViewer.photos[photoViewer.index].filenameUser || photoViewer.photos[photoViewer.index].filename}
          onCloseRequest={() => setPhotoViewer((prev) => ({ ...prev, open: false }))}
          onMovePrevRequest={() => setPhotoViewer((prev) => ({
            ...prev,
            index: (prev.index + prev.photos.length - 1) % prev.photos.length,
          }))}
          onMoveNextRequest={() => setPhotoViewer((prev) => ({
            ...prev,
            index: (prev.index + 1) % prev.photos.length,
          }))}
          enableZoom={true}
        />
      )}
    </div>
  );
}

export default VhuLotsAdminPage;
